<svelte:options
	customElement={{
		tag: "contact-form",
		shadow: "none",
	}}
/>

<script lang="ts">
	import { onMount } from "svelte";
	import ky from "ky";
	import { create, enforce, test, group, skip } from "vest";
	import "vest/enforce/email";
	import { ProblemDocument } from "http-problem-details";

	import { ValidatingStore, type ValidationProblemDetails } from "./validating-store";

	enum FormState {
		Ready = "ready",
		Submitting = "submitting",
		ActionSuccess = "action-success",
		ActionError = "action-error",
	}

	interface ContactRequest {
		fullName: string;
		phone?: string;
		email: string;
		message: string;
	}

	const initialData: ContactRequest = {
		fullName: "",
		phone: "",
		email: "",
		message: "",
	};
	const validationFunc = (data: ContactRequest) => {
		test("fullName", "Bitte geben Sie Ihren Namen ein.", () => {
			enforce(data.fullName).isNotEmpty();
		});
		test("email", "Bitte geben Sie Ihre E-Mail-Adresse ein.", () => {
			enforce(data.email).isNotEmpty();
		});
		test("email", "Bitte geben Sie eine gültige E-Mail-Adresse ein.", () => {
			enforce(data.email).isEmail();
		});
		test("message", "Bitte geben Sie Ihre Mitteilung ein.", () => {
			enforce(data.message).isNotEmpty();
		});
	};

	let state: FormState = FormState.Ready;
	let token: string;
	let store = new ValidatingStore(initialData, validationFunc);
	let errorMessage: string | null = null;
	let isOpen: boolean = false;

	onMount(async () => {
		let tokenElement = document.getElementById("RequestVerificationToken");
		if (tokenElement == null || tokenElement.textContent == null) {
			throw new Error("Could not load XSRF token.");
		}
		token = JSON.parse(tokenElement.textContent);

		document.addEventListener("open-contact-form", () => {
			isOpen = true;
		});
	});

	async function submit() {
		try {
			if (!store.validate()) {
				return;
			}

			state = FormState.Submitting;

			let response = await ky.post(`/api/contact`, {
				headers: { RequestVerificationToken: token },
				json: store.getData(),
				throwHttpErrors: false,
			});

			if (response.ok) {
				state = FormState.ActionSuccess;
			} else if (response.status === 400) {
				state = FormState.Ready;
				let problem = await response.json<ValidationProblemDetails>();
				store.setExternalErrors(problem);
			} else {
				let problem = await response.json<ProblemDocument>();
				errorMessage = problem.detail ?? problem.title;
				state = FormState.ActionError;
			}
		} catch (e) {
			state = FormState.ActionError;
		}
	}

	function close() {
		isOpen = false;
		store.clear();
		errorMessage = null;
		state = FormState.Ready;
	}

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === "Escape" && isOpen) {
			close();
		}
	}
</script>

<svelte:window on:keydown={handleKeydown} />

<div class="modal cta-form-modal animated zoomIn" class:is-active={isOpen}>
	<div class="modal-background"></div>
	{#if (state === FormState.Ready || state === FormState.Submitting) && $store != null}
		<div class="modal-content">
			<p class="mb-2">Nehmen Sie bitte Kontakt mit uns auf, damit wir einen Termin vereinbaren können.</p>
			<div class="field">
				<label class="label">Vor- und Nachname</label>
				<div class="control is-medium">
					<input type="text" class="input" placeholder="Max Muster" autocomplete="name" bind:value={$store.fullName.value} on:blur={() => $store.fullName.touch()} class:is-danger={$store.fullName.hasErrors} />
				</div>
				{#each $store.fullName.errors as error}
					<p class="help is-danger">{error}</p>
				{/each}
			</div>
			<div class="field">
				<label class="label">Telefon</label>
				<div class="control is-medium">
					<input type="tel" class="input" placeholder="033 123 45 67" autocomplete="tel" bind:value={$store.phone.value} on:blur={() => $store.phone.touch()} class:is-danger={$store.phone.hasErrors} />
				</div>
			</div>
			<div class="field">
				<label class="label">E-Mail</label>
				<div class="control is-medium">
					<input type="email" class="input" placeholder="name@muster.ch" autocomplete="email" bind:value={$store.email.value} on:blur={() => $store.email.touch()} class:is-danger={$store.email.hasErrors} />
				</div>
				{#each $store.email.errors as error}
					<p class="help is-danger">{error}</p>
				{/each}
			</div>
			<div class="field">
				<label class="label">Ihre Mitteilung</label>
				<div class="control is-medium">
					<textarea class="textarea" bind:value={$store.message.value} on:blur={() => $store.message.touch()} class:is-danger={$store.message.hasErrors}></textarea>
				</div>
				{#each $store.message.errors as error}
					<p class="help is-danger">{error}</p>
				{/each}
			</div>
			<button class="button is-primary is-medium" class:is-loading={state === FormState.Submitting} on:click={submit}>Senden</button>
		</div>
	{:else if state === FormState.ActionSuccess}
		<div class="modal-content">
			<div class="notification is-success">
				<p class="title is-4 mb-0">Vielen Dank für Ihre Nachricht.</p>
				<button class="button is-dark mt-2" on:click={close}>Schliessen</button>
			</div>
		</div>
	{:else if state === FormState.ActionError}
		<div class="modal-content">
			<div class="notification is-warning">
				<p class="title is-4">Beim Versenden der Nachricht ist ein Fehler aufgetreten.</p>
				<p>Bitte schicken Sie uns stattdessen ein E-Mail an <a href="mailto:info@oswald-schriften.ch">info@oswald-schriften.ch</a></p>
				<p>{errorMessage}</p>

				<button class="button is-dark mt-2" on:click={close}>Schliessen</button>
			</div>
		</div>
	{/if}

	<button class="modal-close is-large" aria-label="close" on:click={close}></button>
</div>
